import {Type} from '@jetbrains/ring-ui/components/list/consts'
import {memo, Suspense, useMemo} from 'react'
import {usePreloadedQuery} from 'react-relay'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import useMyId from '../../../hooks/useMyId'

import type {CommiterSelectQuery} from './__generated__/CommiterSelectQuery.graphql'
import {commiterSelectQuery} from './CommiterSelect.queries'
import UserSelect from './UserSelect'
import type {ChangeCommiter, UserSelectData, UserSelectSelectedItem} from './UserSelect.types'
import {
  getSelectItem,
  sortCommitersByName,
  ALL_REGISTERED_USERS_LABEL,
  ALL_UNREGiSTERED_USERS_LABEL,
  ALL_USERS_SELECT_ITEM,
  getSelectedItem,
} from './UserSelect.utils'

type Props = {
  className?: string
  buttonClassName?: string
  queryRef: PreloadedQuery<CommiterSelectQuery>
  selected: string | null // username
  onChange: (selected: UserSelectSelectedItem) => unknown
}

function CommiterSelect({className, buttonClassName, queryRef, selected, onChange}: Props) {
  const currentUserId = useMyId()
  const {changeCommiters} = usePreloadedQuery(commiterSelectQuery, queryRef)

  const [registeredCommiters, unregisteredCommiters, currentUserCommiters] = useMemo(() => {
    const sortedCommiters = sortCommitersByName(changeCommiters)
    const registered: typeof sortedCommiters = []
    const unregistered: typeof sortedCommiters = []
    const current: typeof sortedCommiters = []
    sortedCommiters.forEach(commiter => {
      const user = commiter.users?.user?.[0]

      if (user?.id !== currentUserId) {
        if (user?.id != null) {
          registered.push(commiter)
        } else {
          unregistered.push(commiter)
        }
      } else {
        current.push(commiter)
      }
    })
    return [registered, unregistered, current]
  }, [changeCommiters, currentUserId])

  const data: UserSelectData = useMemo(() => {
    let items: UserSelectData = [ALL_USERS_SELECT_ITEM]

    if (currentUserCommiters.length > 0) {
      items = items.concat(
        currentUserCommiters.map(commiter =>
          getSelectItem({
            user: commiter.users?.user?.[0],
            vcsUsername: commiter.vcsUsername,
            isCurrentUser: true,
          }),
        ),
      )
    }
    if (registeredCommiters.length > 0) {
      items.push(ALL_REGISTERED_USERS_LABEL)
      items = items.concat(
        registeredCommiters.map(commiter =>
          getSelectItem({user: commiter.users?.user?.[0], vcsUsername: commiter.vcsUsername}),
        ),
      )
    }
    if (unregisteredCommiters.length > 0) {
      items.push(ALL_UNREGiSTERED_USERS_LABEL)
      items = items.concat(
        unregisteredCommiters.map((commiter: ChangeCommiter) =>
          getSelectItem({user: commiter.users?.user?.[0], vcsUsername: commiter.vcsUsername}),
        ),
      )
    }
    if (selected != null) {
      const selectedItem = getSelectedItem(items, selected)
      if (selectedItem == null) {
        if (unregisteredCommiters.length === 0) {
          items.push(ALL_UNREGiSTERED_USERS_LABEL)
        }
        items.push(getSelectItem({vcsUsername: selected}))
      }
    }
    return items
  }, [currentUserCommiters, registeredCommiters, selected, unregisteredCommiters])

  const selectedItem = useMemo(() => getSelectedItem(data, selected), [data, selected])

  return (
    <UserSelect
      className={className}
      buttonClassName={buttonClassName}
      data={data}
      selected={selectedItem}
      onChange={onChange}
      add={{}}
      onAdd={value => onChange(getSelectItem({vcsUsername: value}))}
      filter={{
        fn: (item, string) => {
          if (item.rgItemType === Type.SEPARATOR) {
            const successors = data.slice(
              data.findIndex(otherItem => otherItem.key === item.key) + 1,
            )
            const children = successors.slice(
              0,
              successors.findIndex(successor => successor.rgItemType === Type.SEPARATOR),
            )
            return children.some(child => child.value?.toLowerCase().includes(string))
          }
          return item.value?.toLowerCase().includes(string) ?? false
        },
      }}
    />
  )
}

export default memo(function CommiterSelectContainer(props: Props) {
  const {className, buttonClassName} = props
  return (
    <Suspense
      fallback={
        <UserSelect
          loading
          onChange={() => {}}
          className={className}
          buttonClassName={buttonClassName}
        />
      }
    >
      <CommiterSelect {...props} />
    </Suspense>
  )
})
